import type { FC, PointerEvent as ReactPointerEvent, ThHTMLAttributes } from 'react';
import { useRef } from 'react';
import { ResizeHandle } from './ResizableHeaderCell.styles';

export type ResizableHeaderCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  /** Current width; absent on a column that is not resizable. */
  resizeWidth?: number;
  onResize?: (width: number) => void;
  onResizeEnd?: () => void;
  onResizeReset?: () => void;
};

/**
 * A table header cell whose right edge drags to make the column narrower or
 * wider. Dragging works on the handle alone, so clicking the title still
 * sorts; double-clicking the handle puts the column back to its default.
 */
const ResizableHeaderCell: FC<ResizableHeaderCellProps> = ({
  resizeWidth,
  onResize,
  onResizeEnd,
  onResizeReset,
  children,
  ...rest
}) => {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  if (resizeWidth == null || !onResize) {
    return <th {...rest}>{children}</th>;
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startWidth: resizeWidth };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (!drag.current) return;
    onResize(drag.current.startWidth + event.clientX - drag.current.startX);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (!drag.current) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    drag.current = null;
    onResizeEnd?.();
  };

  return (
    <th {...rest} style={{ ...rest.style, position: 'relative' }}>
      {children}
      <ResizeHandle
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize · double-click to reset"
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onResizeReset?.();
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
    </th>
  );
};

export default ResizableHeaderCell;
